import {Dexie} from 'dexie';

/*
* # IStoredResource
*
* ## RESPONSIBILITIES
*
* Describes the metadata for a stored resource.
*
* */
export interface IStoredResource {
    id: string;
    name: string;
    url: string;
    mimetype: string;
    size?: number; // may be undefined until size has been resolved
}

/*
* # IStoredResource
*
* ## RESPONSIBILITIES
*
* Describes the data stored for each chunk of resource content.
*
* */
export interface IStoredResourceData {
    resourceId: string;
    startOffset: number;
    endOffset: number;
    data: ArrayBuffer;
}

export type IStoredResourceMutationEventCallback = (id: string) => void;

export interface IVanillaReaderStreamedResourceStorage {

    initialize(): Promise<void>;
    hasResource(id: string): Promise<boolean>;

    hasResourceByName(name: string): Promise<boolean>;

    getResource(id: string): Promise<IStoredResource | undefined>;

    getResourceByName(name: string): Promise<IStoredResource | undefined | void>;

    addResource(resource: IStoredResource): Promise<string | void>;

    deleteResource(id: string): Promise<void>;

    deleteResourceByName(name: string): Promise<string | void>;

    addResourceData(
        id: string,
        startOffset: number,
        endOffset: number,
        data: ArrayBuffer,
    ): Promise<void>;

    deleteResourceData(id: string): Promise<number | void>;

    getResourceDataByStartOffset(
        id: string,
        startOffset: number,
    ): Promise<IStoredResourceData | undefined | void>;

    hasResourceDataByStartOffset(id: string, startOffset: number): Promise<boolean | void>;

    onResourceDeleted(callback: IStoredResourceMutationEventCallback): void;

    onResourceAdded(callback: IStoredResourceMutationEventCallback): void;

}

/*
* # VanillaReaderStreamedResourceStorage
*
* ## RESPONSIBILITIES
*
* This class is a very handy, generic store for data that benefits from being stored in chunks, such as data that has
* been streamed from a data source.
*
* It has manages two data types, `IStoredResource` which contains metadata about the resource, and `IStoredResourceData`
* which contains a specific chunk of resource data.
*
**/

export class VanillaReaderStreamedResourceIndexDbStorage extends Dexie implements IVanillaReaderStreamedResourceStorage {

    resource: Dexie.Table<IStoredResource, string>;
    resourceData: Dexie.Table<IStoredResourceData, string>;

    private _resourceDeletedEventCallback: IStoredResourceMutationEventCallback | undefined;
    private _resourceAddedEventCallback: IStoredResourceMutationEventCallback | undefined;
    private _initialized: boolean = false;

    constructor(dbName: string) {
        super(dbName);

        this.version(1).stores({
            resource: 'id, name',
            resourceData: '[resourceId+startOffset], resourceId',

        });

        this.resource = this.table('resource');
        this.resourceData = this.table('resourceData');
    }

    initialize(): Promise<void> {
        return this.open().then(_db => {
            this._initialized = true
        }).catch(_ => {
            this._initialized = false
        });
    }

    onResourceDeleted(callback: IStoredResourceMutationEventCallback): void {
        this._resourceDeletedEventCallback = callback;
    }

    onResourceAdded(callback: IStoredResourceMutationEventCallback): void {
        this._resourceAddedEventCallback = callback;
    }

    getResource(id: string): Promise<IStoredResource | undefined> {
        if (this._initialized) {
            return this.resource.where('id').equals(id).first((res) => {
                return res;
            });
        } else {
            console.warn('No database connection');
            return Promise.resolve(undefined);
        }

    }

    getResourceByName(name: string): Promise<IStoredResource | undefined> {
        if (this._initialized) {
            return this.resource.where('name').equals(name).first((res) => {
                return res;
            });
        } else {
            console.warn('No database connection');
            return Promise.resolve(undefined);
        }
    }

    hasResource(id: string): Promise<boolean> {
        if (this._initialized) {
            return this.resource.where('id').equals(id).count((num) => {
                return num >= 1;
            });
        } else {
            console.warn('No database connection');
            return Promise.resolve(false);
        }
    }

    hasResourceByName(name: string): Promise<boolean> {
        if (this._initialized) {
            return this.resource.where('name').equals(name).count((num) => {
                return num >= 1;
            });
        } else {
            console.warn('No database connection');
            return Promise.resolve(false);
        }
    }


    addResource(resource: IStoredResource): Promise<string | void> {
        if (this._initialized) {
            return this.hasResource(resource.id).then((hasResource: boolean) => {
                if (!hasResource) {
                    return this.resource.add(resource, resource.url).then((res) => {
                        console.log('addResource', res);
                        if (this._resourceAddedEventCallback) {
                            this._resourceAddedEventCallback(resource.id);
                        }
                        return res;
                    });
                } else {
                    console.log('resource exists', resource);
                    return;
                }
            });
        } else {
            console.warn('No database connection');
            return Promise.resolve();
        }
    }

    deleteResource(id: string): Promise<void> {
        if (this._initialized) {
            return this.getResource(id).then((resource: IStoredResource | undefined) => {
                if (resource) {
                    return this.deleteResourceData('publicationId').then(() => {
                        return this.resource.where('id').equals(resource.url).delete().then((_res) => {
                            if (this._resourceDeletedEventCallback) {
                                this._resourceDeletedEventCallback(id);
                            }
                            return Promise.resolve();
                        });
                    });
                } else {
                    console.warn(`IVanillaReaderStreamedResourceStorage.deleteResource(): could not find resource with id "${id}".`);
                    return;
                }
            });
        } else {
            console.warn(`IVanillaReaderStreamedResourceStorage.deleteResource(): No database connection.`);
            return Promise.resolve();
        }
    }

    deleteResourceByName(name: string): Promise<string | void> {
        if (this._initialized) {
            return this.getResourceByName(name).then((resource: IStoredResource | undefined) => {
                if (resource) {
                    return this.deleteResourceData(resource.url).then(() => {
                        return this.resource.where('name').equals(name).delete().then((_res) => {
                            if (this._resourceDeletedEventCallback) {
                                this._resourceDeletedEventCallback(name);
                            }
                            return Promise.resolve();
                        });
                    });
                } else {

                    return;
                }
            });
        } else {
            console.warn(`IVanillaReaderStreamedResourceStorage.deleteResourceByName(): No database connection.`);
            return Promise.resolve();
        }
    }

    addResourceData(
        id: string,
        startOffset: number,
        endOffset: number,
        data: ArrayBuffer,
    ): Promise<void> {
        if (this._initialized) {
            const resourceData: IStoredResourceData = {
                resourceId: id,
                startOffset: startOffset,
                endOffset: endOffset,
                data: data,
            };
            return this.resourceData.add(resourceData, id).catch((err) => {
                console.log('Exception in ResourceStorage.addResourceData: ', err);
                throw err;
            }).then(() => {});
        } else {
            console.error('Exception in ResourceStorage.addResourceData: No database connection');
            return Promise.reject();
        }
    }

    getResourceDataByStartOffset(id: string, startOffset: number): Promise<IStoredResourceData | undefined> {
        if (this._initialized) {
            return this.resourceData.where('[resourceId+startOffset]').equals([id, startOffset]).first((dataChunk) => {
                return dataChunk;
            });
        } else {
            return Promise.resolve(undefined);
        }
    }

    hasResourceDataByStartOffset(id: string, startOffset: number): Promise<boolean | void> {
        if (this._initialized) {
            return this.resourceData.where('[resourceId+startOffset]').equals([id, startOffset]).count().then((num) => {
                return num >= 1;
            }).catch(err => {
                console.error('Exception in ResourceStorage.hasResourceDataByStartOffset("' + id + '", "' + startOffset + '")', err);
                throw err;
            });
        } else {
            console.error('Exception in ResourceStorage.hasResourceDataByStartOffset("' + id + '", "' + startOffset + '"): No database connection');
            return Promise.reject();
        }
    }

    deleteResourceData(id: string): Promise<number | void> {
        if (this._initialized) {
            return this.resourceData.where('resourceId').equals(id).delete().catch((err) => {
                console.error('Exception in ResourceStorage.deleteResourceData("' + id + '")', err);
                throw err;
            });
        } else {
            console.error('Exception in ResourceStorage.deleteResourceData("' + id + '"): No database connection');
            return Promise.reject();
        }
    }

}
